// Minimal i18n for the home screen (campaign list + shell).
// The marketplace passes the active language via ?lang= on the iframe URL.
// We read it once at module load — the language doesn't change during the
// app's lifetime.

export type Lang = 'en' | 'nl' | 'fr' | 'de';

const SUPPORTED: Lang[] = ['en', 'nl', 'fr', 'de'];

function detectLang(): Lang {
  const param = new URLSearchParams(window.location.search).get('lang');
  if (param && SUPPORTED.includes(param as Lang)) return param as Lang;
  return 'en';
}

export const lang: Lang = detectLang();

const T: Record<string, Record<Lang, string>> = {
  // Topbar / shell
  'nav.campaigns': {
    en: 'Campaigns',
    nl: 'Campagnes',
    fr: 'Campagnes',
    de: 'Kampagnen',
  },
  'nav.costCalculator': {
    en: 'Cost calculator',
    nl: 'Kostencalculator',
    fr: 'Calculateur de coûts',
    de: 'Kostenrechner',
  },
  'nav.contentGenerator': {
    en: 'Content generator',
    nl: 'Content generator',
    fr: 'Générateur de contenu',
    de: 'Content-Generator',
  },
  'shell.loggedInAs': {
    en: 'Logged in as',
    nl: 'Ingelogd als',
    fr: 'Connecté en tant que',
    de: 'Angemeldet als',
  },
  'shell.noAccessRoles': {
    en: 'You do not have access to ViralLoop. Ask your organisation admin to grant the Campaign Manager or Campaign Viewer role.',
    nl: 'Je hebt geen toegang tot ViralLoop. Vraag je organisatiebeheerder om de rol Campaign Manager of Campaign Viewer toe te kennen.',
    fr: "Vous n'avez pas accès à ViralLoop. Demandez à l'administrateur de votre organisation de vous attribuer le rôle Campaign Manager ou Campaign Viewer.",
    de: 'Du hast keinen Zugriff auf ViralLoop. Bitte deinen Organisations-Admin, dir die Rolle Campaign Manager oder Campaign Viewer zuzuweisen.',
  },

  // Dashboard (home)
  'dashboard.title': {
    en: 'Campaigns',
    nl: 'Campagnes',
    fr: 'Campagnes',
    de: 'Kampagnen',
  },
  'dashboard.new': {
    en: '+ New campaign',
    nl: '+ Nieuwe campagne',
    fr: '+ Nouvelle campagne',
    de: '+ Neue Kampagne',
  },
  'dashboard.empty': {
    en: 'No campaigns yet. Create your first viral campaign.',
    nl: 'Nog geen campagnes. Maak je eerste virale campagne aan.',
    fr: 'Aucune campagne pour le moment. Créez votre première campagne virale.',
    de: 'Noch keine Kampagnen. Erstelle deine erste virale Kampagne.',
  },

  // Common
  'common.loading': {
    en: 'Loading…',
    nl: 'Laden…',
    fr: 'Chargement…',
    de: 'Lädt…',
  },
  'common.detail': {
    en: 'Detail →',
    nl: 'Detail →',
    fr: 'Détail →',
    de: 'Detail →',
  },

  // Table column headers
  'col.name': {
    en: 'Name',
    nl: 'Naam',
    fr: 'Nom',
    de: 'Name',
  },
  'col.status': {
    en: 'Status',
    nl: 'Status',
    fr: 'Statut',
    de: 'Status',
  },
  'col.trigger': {
    en: 'Trigger',
    nl: 'Trigger',
    fr: 'Déclencheur',
    de: 'Auslöser',
  },
  'col.signups': {
    en: 'Signups',
    nl: 'Inschrijvingen',
    fr: 'Inscriptions',
    de: 'Anmeldungen',
  },
  'col.activated': {
    en: 'Activated',
    nl: 'Geactiveerd',
    fr: 'Activé',
    de: 'Aktiviert',
  },
  'col.costToDate': {
    en: 'Cost to date',
    nl: 'Kost t.t.v.',
    fr: 'Coût à ce jour',
    de: 'Kosten bis dato',
  },
  'col.endsAt': {
    en: 'Ends',
    nl: 'Eindigt',
    fr: 'Fin',
    de: 'Endet',
  },

  // Campaign status badges
  'status.draft': {
    en: 'draft',
    nl: 'concept',
    fr: 'brouillon',
    de: 'entwurf',
  },
  'status.active': {
    en: 'active',
    nl: 'actief',
    fr: 'active',
    de: 'aktiv',
  },
  'status.paused': {
    en: 'paused',
    nl: 'gepauzeerd',
    fr: 'en pause',
    de: 'pausiert',
  },
  'status.ended': {
    en: 'ended',
    nl: 'beëindigd',
    fr: 'terminée',
    de: 'beendet',
  },

  // ─── Common buttons / generic ────────────────────────────────────
  'common.previous': { en: 'Previous', nl: 'Vorige', fr: 'Précédent', de: 'Zurück' },
  'common.next': { en: 'Next', nl: 'Volgende', fr: 'Suivant', de: 'Weiter' },
  'common.close': { en: 'Close', nl: 'Sluit', fr: 'Fermer', de: 'Schließen' },
  'common.delete': { en: 'Delete', nl: 'Verwijder', fr: 'Supprimer', de: 'Löschen' },
  'common.edit': { en: 'Edit', nl: 'Bewerk', fr: 'Modifier', de: 'Bearbeiten' },
  'common.done': { en: 'Done', nl: 'Klaar', fr: 'Terminé', de: 'Fertig' },
  'common.optional': { en: 'optional', nl: 'optioneel', fr: 'optionnel', de: 'optional' },
  'common.yes': { en: 'Yes', nl: 'Ja', fr: 'Oui', de: 'Ja' },
  'common.no': { en: 'No', nl: 'Nee', fr: 'Non', de: 'Nein' },

  // ─── Wizard shell ────────────────────────────────────────────────
  'wizard.titleStep': {
    en: 'New campaign — step {step}/{total}',
    nl: 'Nieuwe campagne — stap {step}/{total}',
    fr: 'Nouvelle campagne — étape {step}/{total}',
    de: 'Neue Kampagne — Schritt {step}/{total}',
  },
  'wizard.saveDraft': {
    en: 'Save as draft',
    nl: 'Opslaan als draft',
    fr: 'Enregistrer comme brouillon',
    de: 'Als Entwurf speichern',
  },
  'wizard.launch': {
    en: 'Launch now',
    nl: 'Nu lanceren',
    fr: 'Lancer maintenant',
    de: 'Jetzt starten',
  },

  // ─── Step 1: basics ──────────────────────────────────────────────
  'step1.campaignName': {
    en: 'Campaign name',
    nl: 'Campagne naam',
    fr: 'Nom de la campagne',
    de: 'Kampagnenname',
  },
  'step1.campaignNamePlaceholder': {
    en: 'Q2 launch',
    nl: 'Q2 launch',
    fr: 'Lancement Q2',
    de: 'Q2 Launch',
  },
  'step1.productName': {
    en: 'Product name',
    nl: 'Product naam',
    fr: 'Nom du produit',
    de: 'Produktname',
  },
  'step1.productNamePlaceholder': {
    en: 'My App',
    nl: 'Mijn App',
    fr: 'Mon App',
    de: 'Meine App',
  },
  'step1.productDescription': {
    en: 'Product description',
    nl: 'Product beschrijving',
    fr: 'Description du produit',
    de: 'Produktbeschreibung',
  },
  'step1.targetAudience': {
    en: 'Target audience',
    nl: 'Doelgroep',
    fr: 'Public cible',
    de: 'Zielgruppe',
  },
  'step1.registrationUrl': {
    en: 'Registration URL',
    nl: 'Registratie URL',
    fr: "URL d'inscription",
    de: 'Registrierungs-URL',
  },
  'step1.urlHintPrefix': {
    en: 'The platform automatically appends',
    nl: 'Het platform voegt automatisch',
    fr: 'La plateforme ajoute automatiquement',
    de: 'Die Plattform fügt automatisch',
  },
  'step1.urlHintSuffix': {
    en: 'to the URL.',
    nl: 'toe.',
    fr: 'à l\'URL.',
    de: 'an die URL an.',
  },
  'step1.triggerEvent': {
    en: 'Trigger event',
    nl: 'Trigger event',
    fr: 'Événement déclencheur',
    de: 'Auslöser-Event',
  },

  // Trigger event labels (used in wizard dropdown + summaries)
  'triggerEvent.platform_registration': {
    en: 'Platform registration',
    nl: 'Platform registratie',
    fr: 'Inscription à la plateforme',
    de: 'Plattform-Registrierung',
  },
  'triggerEvent.app_installation': {
    en: 'App installation',
    nl: 'App installatie',
    fr: "Installation de l'app",
    de: 'App-Installation',
  },
  'triggerEvent.first_payment': {
    en: 'First payment',
    nl: 'Eerste betaling',
    fr: 'Premier paiement',
    de: 'Erste Zahlung',
  },
  'triggerEvent.login_milestone': {
    en: 'Login milestone',
    nl: 'Login milestone',
    fr: 'Étape de connexion',
    de: 'Login-Meilenstein',
  },
  'triggerEvent.plan_upgrade': {
    en: 'Plan upgrade',
    nl: 'Plan upgrade',
    fr: 'Mise à niveau du plan',
    de: 'Plan-Upgrade',
  },
  'triggerEvent.review_submitted': {
    en: 'Review submitted',
    nl: 'Review geplaatst',
    fr: 'Avis soumis',
    de: 'Bewertung abgegeben',
  },
  'triggerEvent.custom': {
    en: 'Custom event',
    nl: 'Custom event',
    fr: 'Événement personnalisé',
    de: 'Benutzerdefiniertes Event',
  },

  // ─── Step 2: timing ──────────────────────────────────────────────
  'step2.startsAt': {
    en: 'Start date',
    nl: 'Startdatum',
    fr: 'Date de début',
    de: 'Startdatum',
  },
  'step2.duration': {
    en: 'Duration (days):',
    nl: 'Duur (dagen):',
    fr: 'Durée (jours):',
    de: 'Dauer (Tage):',
  },
  'step2.luckyDrawRatio': {
    en: 'Lucky draw ratio:',
    nl: 'Lucky draw ratio:',
    fr: 'Ratio tirage au sort:',
    de: 'Lucky-Draw-Verhältnis:',
  },
  'step2.luckyDrawOff': {
    en: 'off',
    nl: 'uit',
    fr: 'désactivé',
    de: 'aus',
  },
  'step2.luckyDrawOn': {
    en: '1 in {n}',
    nl: '1 op {n}',
    fr: '1 sur {n}',
    de: '1 von {n}',
  },
  'step2.luckyDrawHint': {
    en: '0 = disabled. Max 1 in 50.',
    nl: '0 = uitgeschakeld. Max 1/50.',
    fr: '0 = désactivé. Max 1 sur 50.',
    de: '0 = deaktiviert. Max 1/50.',
  },
  'step2.endsOn': {
    en: 'Ends on',
    nl: 'Eindigt op',
    fr: 'Se termine le',
    de: 'Endet am',
  },

  // ─── Reward configurator ─────────────────────────────────────────
  'rewards.title': { en: 'Rewards', nl: 'Rewards', fr: 'Récompenses', de: 'Belohnungen' },
  'rewards.add': {
    en: '+ Add reward',
    nl: '+ Voeg reward toe',
    fr: '+ Ajouter une récompense',
    de: '+ Belohnung hinzufügen',
  },
  'rewards.empty': {
    en: 'Add at least one reward to be able to launch the campaign.',
    nl: 'Voeg minstens één reward toe om de campagne te kunnen lanceren.',
    fr: 'Ajoutez au moins une récompense pour pouvoir lancer la campagne.',
    de: 'Füge mindestens eine Belohnung hinzu, um die Kampagne starten zu können.',
  },
  'rewards.editTitle': {
    en: 'Edit reward #{n}',
    nl: 'Reward #{n} bewerken',
    fr: 'Modifier la récompense #{n}',
    de: 'Belohnung #{n} bearbeiten',
  },
  'rewards.noLabel': {
    en: '(no label)',
    nl: '(geen label)',
    fr: '(pas de libellé)',
    de: '(keine Bezeichnung)',
  },
  'rewards.moveUp': { en: 'Move up', nl: 'Naar boven', fr: 'Monter', de: 'Nach oben' },
  'rewards.moveDown': { en: 'Move down', nl: 'Naar onder', fr: 'Descendre', de: 'Nach unten' },

  // Trigger types (radio/select labels in wizard + tier summaries)
  'triggerType.lucky_draw': {
    en: 'Lucky draw',
    nl: 'Lucky draw',
    fr: 'Tirage au sort',
    de: 'Lucky Draw',
  },
  'triggerType.referral_milestone': {
    en: 'Referral milestone',
    nl: 'Referral milestone',
    fr: 'Palier de parrainage',
    de: 'Empfehlungs-Meilenstein',
  },
  'triggerType.top_n_daily': {
    en: 'Top N — Daily',
    nl: 'Top N — Dagelijks',
    fr: 'Top N — Quotidien',
    de: 'Top N — Täglich',
  },
  'triggerType.top_n_weekly': {
    en: 'Top N — Weekly',
    nl: 'Top N — Wekelijks',
    fr: 'Top N — Hebdomadaire',
    de: 'Top N — Wöchentlich',
  },
  'triggerType.top_n_monthly': {
    en: 'Top N — Monthly',
    nl: 'Top N — Maandelijks',
    fr: 'Top N — Mensuel',
    de: 'Top N — Monatlich',
  },
  'triggerType.top_n_overall': {
    en: 'Top N — Final standings',
    nl: 'Top N — Eindstand',
    fr: 'Top N — Classement final',
    de: 'Top N — Endstand',
  },

  // Reward types
  'rewardType.voucher_code': {
    en: 'Voucher code',
    nl: 'Voucher code',
    fr: 'Code bon',
    de: 'Gutscheincode',
  },
  'rewardType.platform_credits': {
    en: 'Platform credits',
    nl: 'Platform credits',
    fr: 'Crédits plateforme',
    de: 'Plattform-Credits',
  },
  'rewardType.custom': {
    en: 'Custom',
    nl: 'Custom',
    fr: 'Personnalisé',
    de: 'Benutzerdefiniert',
  },

  // Reward field labels
  'rewardField.triggerType': {
    en: 'Trigger type',
    nl: 'Trigger type',
    fr: 'Type de déclencheur',
    de: 'Auslöser-Typ',
  },
  'rewardField.referralCount': {
    en: 'Number of referrals',
    nl: 'Aantal referrals',
    fr: 'Nombre de parrainages',
    de: 'Anzahl Empfehlungen',
  },
  'rewardField.topN': {
    en: 'Top N (number of winners)',
    nl: 'Top N (aantal winnaars)',
    fr: 'Top N (nombre de gagnants)',
    de: 'Top N (Anzahl Gewinner)',
  },
  'rewardField.cumulative': {
    en: 'Cumulative?',
    nl: 'Cumulatief?',
    fr: 'Cumulatif ?',
    de: 'Kumulativ?',
  },
  'rewardField.cumulativeYes': {
    en: 'Yes — winners can win multiple times',
    nl: 'Ja — winnaars kunnen meerdere keren winnen',
    fr: "Oui — les gagnants peuvent gagner plusieurs fois",
    de: 'Ja — Gewinner können mehrfach gewinnen',
  },
  'rewardField.cumulativeNo': {
    en: 'No — one-time win',
    nl: 'Nee — éénmaal winbaar',
    fr: 'Non — gain unique',
    de: 'Nein — einmaliger Gewinn',
  },
  'rewardField.luckyDrawRatio': {
    en: 'Custom ratio (overrides campaign {ratio}). Empty = campaign ratio.',
    nl: 'Eigen ratio (overschrijft campagne {ratio}). Leeg = campagne ratio.',
    fr: "Ratio personnalisé (remplace celui de la campagne {ratio}). Vide = ratio de la campagne.",
    de: 'Eigenes Verhältnis (überschreibt Kampagnen-{ratio}). Leer = Kampagnenwert.',
  },
  'rewardField.rewardType': {
    en: 'Reward type',
    nl: 'Reward type',
    fr: 'Type de récompense',
    de: 'Belohnungstyp',
  },
  'rewardField.label': {
    en: 'Label',
    nl: 'Label',
    fr: 'Libellé',
    de: 'Bezeichnung',
  },
  'rewardField.labelPlaceholder': {
    en: 'e.g. 1 month Claude Pro',
    nl: 'bijv. 1 maand Claude Pro',
    fr: 'ex. 1 mois Claude Pro',
    de: 'z.B. 1 Monat Claude Pro',
  },
  'rewardField.descriptionLabel': {
    en: 'Description (optional)',
    nl: 'Beschrijving (optioneel)',
    fr: 'Description (optionnelle)',
    de: 'Beschreibung (optional)',
  },
  'rewardField.nominalValue': {
    en: 'Nominal value (€)',
    nl: 'Nominale waarde (€)',
    fr: 'Valeur nominale (€)',
    de: 'Nennwert (€)',
  },
  'rewardField.realCost': {
    en: 'Real cost (€)',
    nl: 'Werkelijke kost (€)',
    fr: 'Coût réel (€)',
    de: 'Tatsächliche Kosten (€)',
  },
  'rewardField.realCostPlaceholder': {
    en: 'same as nominal',
    nl: 'zelfde als nominaal',
    fr: 'identique à la valeur nominale',
    de: 'gleich wie Nennwert',
  },
  'rewardField.maxWinners': {
    en: 'Max winners (empty = unlimited)',
    nl: 'Max winners (leeg = onbeperkt)',
    fr: 'Gagnants max (vide = illimité)',
    de: 'Max. Gewinner (leer = unbegrenzt)',
  },
  'rewardField.maxWinnersPlaceholder': {
    en: 'unlimited',
    nl: 'onbeperkt',
    fr: 'illimité',
    de: 'unbegrenzt',
  },
  'rewardField.maxWinnersHint': {
    en: 'Stop this reward after X winners — campaign keeps running.',
    nl: 'Stop deze reward na X winnaars — campagne loopt verder.',
    fr: 'Arrête cette récompense après X gagnants — la campagne continue.',
    de: 'Stoppt diese Belohnung nach X Gewinnern — Kampagne läuft weiter.',
  },

  // Per-trigger summary in collapsed reward row
  'rewardSummary.luckyDraw': {
    en: '1 in {n}',
    nl: '1 op {n}',
    fr: '1 sur {n}',
    de: '1 von {n}',
  },
  'rewardSummary.referrals': {
    en: '{n} referrals',
    nl: '{n} referrals',
    fr: '{n} parrainages',
    de: '{n} Empfehlungen',
  },
  'rewardSummary.topN': {
    en: 'Top {n}',
    nl: 'Top {n}',
    fr: 'Top {n}',
    de: 'Top {n}',
  },

  // ─── Step 4: Budget ──────────────────────────────────────────────
  'budget.title': {
    en: 'Budget control',
    nl: 'Budget controle',
    fr: 'Contrôle du budget',
    de: 'Budgetkontrolle',
  },
  'budget.subtitle': {
    en: 'Configure up to 3 layers of budget protection. You can combine all layers or use only one.',
    nl: 'Configureer maximaal 3 lagen van budget bescherming. Je kan alle lagen combineren of slechts één gebruiken.',
    fr: "Configurez jusqu'à 3 niveaux de protection budgétaire. Vous pouvez combiner toutes les couches ou n'en utiliser qu'une.",
    de: 'Konfiguriere bis zu 3 Schichten von Budgetschutz. Du kannst alle Schichten kombinieren oder nur eine verwenden.',
  },
  'budget.layer1Title': {
    en: '🏅 Layer 1 — Per reward track (max winners)',
    nl: '🏅 Laag 1 — Per reward track (max winners)',
    fr: '🏅 Niveau 1 — Par récompense (max. gagnants)',
    de: '🏅 Schicht 1 — Pro Belohnung (max. Gewinner)',
  },
  'budget.layer1Description': {
    en: 'Limit the number of winners per reward. When the maximum is reached that reward stops, but the campaign keeps running. Set this per reward in the previous step using "Max winners".',
    nl: 'Beperk het aantal winnaars per reward. Als het maximum bereikt is, stopt die reward maar loopt de campagne verder. Stel dit per reward in in stap 3 via het veld Max winners.',
    fr: 'Limitez le nombre de gagnants par récompense. Lorsque le maximum est atteint, cette récompense s\'arrête mais la campagne continue. Définissez-le par récompense à l\'étape précédente via "Gagnants max".',
    de: 'Beschränke die Anzahl der Gewinner pro Belohnung. Sobald das Maximum erreicht ist, stoppt diese Belohnung, aber die Kampagne läuft weiter. Lege dies pro Belohnung im vorherigen Schritt über "Max. Gewinner" fest.',
  },
  'budget.layer1Configured': {
    en: '✅ {set} of {total} rewards have a max winners set.',
    nl: '✅ {set} van {total} rewards hebben een max winners ingesteld.',
    fr: '✅ {set} sur {total} récompenses ont un max de gagnants défini.',
    de: '✅ {set} von {total} Belohnungen haben max. Gewinner gesetzt.',
  },
  'budget.layer1NotConfigured': {
    en: '⚠️ No rewards have a max winners set. Optional but recommended.',
    nl: '⚠️ Geen rewards met max winners ingesteld. Optioneel maar aanbevolen.',
    fr: '⚠️ Aucune récompense n\'a de max gagnants défini. Optionnel mais recommandé.',
    de: '⚠️ Keine Belohnungen mit max. Gewinnern. Optional, aber empfohlen.',
  },
  'budget.layer2Title': {
    en: '📅 Layer 2 — Daily maximum budget',
    nl: '📅 Laag 2 — Dagelijks maximumbudget',
    fr: '📅 Niveau 2 — Budget maximum quotidien',
    de: '📅 Schicht 2 — Tägliches Maximalbudget',
  },
  'budget.dailyToggle': {
    en: 'Enable daily cap',
    nl: 'Dagelijks cap inschakelen',
    fr: 'Activer le plafond quotidien',
    de: 'Tägliches Limit aktivieren',
  },
  'budget.dailyAmount': {
    en: 'Maximum per day (€)',
    nl: 'Maximum per dag (€)',
    fr: 'Maximum par jour (€)',
    de: 'Maximum pro Tag (€)',
  },
  'budget.dailyHint': {
    en: 'Once the daily cap is reached, lucky draws stop for the rest of the day. The counter resets the next day. Milestones and top-N rewards always continue.',
    nl: 'Bij het bereiken van de cap stoppen lucky draws voor de rest van de dag. De volgende dag reset de teller. Milestones en top-N rewards lopen altijd door.',
    fr: 'Une fois le plafond atteint, les tirages au sort s\'arrêtent jusqu\'au lendemain. Les paliers et récompenses top-N continuent.',
    de: 'Wenn das Tageslimit erreicht ist, stoppen Lucky Draws für den Rest des Tages. Am nächsten Tag wird der Zähler zurückgesetzt. Meilensteine und Top-N-Belohnungen laufen immer weiter.',
  },
  'budget.layer3Title': {
    en: '💰 Layer 3 — Total maximum budget',
    nl: '💰 Laag 3 — Totaal maximumbudget',
    fr: '💰 Niveau 3 — Budget maximum total',
    de: '💰 Schicht 3 — Maximales Gesamtbudget',
  },
  'budget.totalToggle': {
    en: 'Enable total budget',
    nl: 'Totaalbudget inschakelen',
    fr: 'Activer le budget total',
    de: 'Gesamtbudget aktivieren',
  },
  'budget.totalAmount': {
    en: 'Maximum budget (€)',
    nl: 'Maximumbudget (€)',
    fr: 'Budget maximum (€)',
    de: 'Maximalbudget (€)',
  },
  'budget.softCap': {
    en: 'Warn at {pct}% (≈ €{amount})',
    nl: 'Waarschuwen vanaf {pct}% (≈ €{amount})',
    fr: 'Alerte à {pct}% (≈ €{amount})',
    de: 'Warnen bei {pct}% (≈ €{amount})',
  },
  'budget.hardCapAction': {
    en: 'Action when the maximum budget is reached',
    nl: 'Actie bij bereiken van het maximumbudget',
    fr: 'Action à effectuer lorsque le budget max. est atteint',
    de: 'Aktion bei Erreichen des Maximalbudgets',
  },
  'budget.hardCap.stop_lucky_draw_only': {
    en: '🎲 Stop only lucky draw — campaign continues (recommended)',
    nl: '🎲 Alleen lucky draw stoppen — campagne loopt verder (aanbevolen)',
    fr: '🎲 Arrêter seulement le tirage — la campagne continue (recommandé)',
    de: '🎲 Nur Lucky Draw stoppen — Kampagne läuft weiter (empfohlen)',
  },
  'budget.hardCap.pause_campaign': {
    en: '⏸ Pause the entire campaign',
    nl: '⏸ Volledige campagne pauzeren',
    fr: '⏸ Mettre toute la campagne en pause',
    de: '⏸ Gesamte Kampagne pausieren',
  },
  'budget.hardCap.notify_only': {
    en: '🔔 Just notify — I decide what to do',
    nl: '🔔 Alleen notificatie sturen — ik beslis zelf',
    fr: '🔔 Notifier seulement — je décide moi-même',
    de: '🔔 Nur benachrichtigen — ich entscheide selbst',
  },
  'budget.hint.stop_lucky_draw_only': {
    en: 'Lucky draws stop, but participants keep collecting referrals for guaranteed rewards and the leaderboard. Momentum is preserved.',
    nl: 'De lucky draw stopt, maar deelnemers blijven referrals verzamelen voor gegarandeerde rewards en het leaderboard. Momentum blijft behouden.',
    fr: "Les tirages au sort s'arrêtent, mais les participants continuent à collecter des parrainages pour les récompenses garanties et le classement. La dynamique est préservée.",
    de: 'Lucky Draws stoppen, aber Teilnehmer sammeln weiter Empfehlungen für garantierte Belohnungen und das Leaderboard. Momentum bleibt erhalten.',
  },
  'budget.hint.pause_campaign': {
    en: 'The whole campaign is paused. Participants who just shared a link can no longer take part. This may damage trust.',
    nl: 'De volledige campagne wordt gepauzeerd. Deelnemers die net een link deelden kunnen niet meer meedoen. Dit kan het vertrouwen schaden.',
    fr: "La campagne entière est suspendue. Les participants qui viennent de partager un lien ne peuvent plus participer. Cela peut nuire à la confiance.",
    de: 'Die gesamte Kampagne wird pausiert. Teilnehmer, die gerade einen Link geteilt haben, können nicht mehr teilnehmen. Das kann Vertrauen kosten.',
  },
  'budget.hint.notify_only': {
    en: 'You receive a notification but the campaign keeps running. You decide when to intervene.',
    nl: 'Je ontvangt een notificatie maar de campagne loopt gewoon door. Jij beslist wanneer je ingrijpt.',
    fr: 'Vous recevez une notification mais la campagne continue. Vous décidez quand intervenir.',
    de: 'Du erhältst eine Benachrichtigung, aber die Kampagne läuft weiter. Du entscheidest, wann du eingreifst.',
  },

  // ─── Step 5: Summary + simulation ────────────────────────────────
  'summary.title': {
    en: 'Summary',
    nl: 'Samenvatting',
    fr: 'Résumé',
    de: 'Zusammenfassung',
  },
  'summary.product': { en: 'Product', nl: 'Product', fr: 'Produit', de: 'Produkt' },
  'summary.start': { en: 'Starts', nl: 'Start', fr: 'Début', de: 'Start' },
  'summary.ends': { en: 'Ends', nl: 'Eindigt', fr: 'Fin', de: 'Endet' },
  'summary.luckyDraw': {
    en: 'Lucky draw',
    nl: 'Lucky draw',
    fr: 'Tirage au sort',
    de: 'Lucky Draw',
  },
  'summary.rewardCount': {
    en: 'Number of rewards',
    nl: 'Aantal rewards',
    fr: 'Nombre de récompenses',
    de: 'Anzahl Belohnungen',
  },
  'summary.perRewardMax': {
    en: 'Per reward max',
    nl: 'Per reward max',
    fr: 'Max par récompense',
    de: 'Max pro Belohnung',
  },
  'summary.perRewardMaxValue': {
    en: '{n} configured',
    nl: '{n} ingesteld',
    fr: '{n} configurés',
    de: '{n} eingestellt',
  },
  'summary.none': { en: 'none', nl: 'geen', fr: 'aucun', de: 'keine' },
  'summary.dailyCap': {
    en: 'Daily cap',
    nl: 'Dagelijks cap',
    fr: 'Plafond quotidien',
    de: 'Tageslimit',
  },
  'summary.dailyCapValue': {
    en: '€{amount}/day',
    nl: '€{amount}/dag',
    fr: '€{amount}/jour',
    de: '€{amount}/Tag',
  },
  'summary.totalBudget': {
    en: 'Total budget',
    nl: 'Totaalbudget',
    fr: 'Budget total',
    de: 'Gesamtbudget',
  },
  'summary.totalBudgetValue': {
    en: '€{total} (warn at €{soft}, {action})',
    nl: '€{total} (waarschuwing bij €{soft}, {action})',
    fr: '€{total} (alerte à €{soft}, {action})',
    de: '€{total} (Warnung bei €{soft}, {action})',
  },

  'sim.title': {
    en: 'Cost simulation',
    nl: 'Kostensimulatie',
    fr: 'Simulation de coûts',
    de: 'Kostensimulation',
  },
  'sim.description': {
    en: 'Estimate the total reward cost before launching. Play with the inputs to see how the cost scales.',
    nl: 'Schat de totale rewardkost vóór je lanceert. Speel met de inputs om te zien hoe de kost meeschaalt.',
    fr: 'Estimez le coût total des récompenses avant de lancer. Jouez avec les paramètres pour voir l\'évolution.',
    de: 'Schätze die Gesamtkosten der Belohnungen vor dem Start. Spiele mit den Eingaben, um zu sehen, wie sich die Kosten skalieren.',
  },
  'sim.targetSignups': {
    en: 'Target signups',
    nl: 'Doel inschrijvingen',
    fr: "Objectif d'inscriptions",
    de: 'Ziel Anmeldungen',
  },
  'sim.milestoneRates': {
    en: 'Conversion rate per milestone',
    nl: 'Conversieratio per milestone',
    fr: 'Taux de conversion par palier',
    de: 'Konversionsrate pro Meilenstein',
  },
  'sim.milestoneSliderLabel': {
    en: '{n} referrals — {pct}%',
    nl: '{n} referrals — {pct}%',
    fr: '{n} parrainages — {pct}%',
    de: '{n} Empfehlungen — {pct}%',
  },
  'sim.col.reward': { en: 'Reward', nl: 'Reward', fr: 'Récompense', de: 'Belohnung' },
  'sim.col.winners': { en: 'Winners', nl: 'Winnaars', fr: 'Gagnants', de: 'Gewinner' },
  'sim.col.costPerWinner': {
    en: 'Cost / winner',
    nl: 'Kost / winnaar',
    fr: 'Coût / gagnant',
    de: 'Kosten / Gewinner',
  },
  'sim.col.total': { en: 'Total', nl: 'Totaal', fr: 'Total', de: 'Gesamt' },
  'sim.totalRow': { en: 'Total', nl: 'Totaal', fr: 'Total', de: 'Gesamt' },
  'sim.perSignupRow': {
    en: 'Cost per signup',
    nl: 'Kost per inschrijving',
    fr: 'Coût par inscription',
    de: 'Kosten pro Anmeldung',
  },
  'sim.cap': { en: 'cap', nl: 'cap', fr: 'plafond', de: 'cap' },
  'sim.capTitle': {
    en: 'Capped by max winners',
    nl: 'Gecapt door max winners',
    fr: 'Limité par le max de gagnants',
    de: 'Durch max. Gewinner begrenzt',
  },
  'sim.capNotice': {
    en: 'One or more rewards are capped by their max winners. Participants beyond that count won\'t receive that reward.',
    nl: 'Eén of meer rewards zijn gecapt door hun max winners. Deelnemers boven dat aantal krijgen die reward niet meer.',
    fr: "Une ou plusieurs récompenses sont limitées par leur max de gagnants. Les participants au-delà ne la recevront plus.",
    de: 'Eine oder mehrere Belohnungen sind durch ihre max. Gewinner begrenzt. Teilnehmer darüber hinaus erhalten sie nicht.',
  },
  'sim.budgetExceeded': {
    en: '⚠ Projected cost (€{cost}) exceeds your total budget of €{budget}. At hard cap, the action {action} will run.',
    nl: '⚠ Geprojecteerde kost (€{cost}) overschrijdt je totaalbudget van €{budget}. Bij hard cap wordt actie {action} uitgevoerd.',
    fr: '⚠ Coût projeté (€{cost}) dépasse le budget total de €{budget}. Au plafond strict, l\'action {action} sera déclenchée.',
    de: '⚠ Prognostizierte Kosten (€{cost}) überschreiten dein Gesamtbudget von €{budget}. Bei hartem Limit läuft die Aktion {action}.',
  },
  'sim.empty.noRewards': {
    en: 'Add rewards in step 3 to see the simulation.',
    nl: 'Voeg rewards toe in stap 3 om de simulatie te zien.',
    fr: 'Ajoutez des récompenses à l\'étape 3 pour voir la simulation.',
    de: 'Füge in Schritt 3 Belohnungen hinzu, um die Simulation zu sehen.',
  },
  'sim.empty.noTarget': {
    en: 'Enter a target to see the simulation.',
    nl: 'Vul een doel in om de simulatie te zien.',
    fr: 'Indiquez un objectif pour voir la simulation.',
    de: 'Gib ein Ziel ein, um die Simulation zu sehen.',
  },

  'scenario.conservative': {
    en: 'Conservative',
    nl: 'Conservatief',
    fr: 'Conservateur',
    de: 'Konservativ',
  },
  'scenario.expected': {
    en: 'Expected',
    nl: 'Verwacht',
    fr: 'Attendu',
    de: 'Erwartet',
  },
  'scenario.optimistic': {
    en: 'Optimistic',
    nl: 'Optimistisch',
    fr: 'Optimiste',
    de: 'Optimistisch',
  },
  'scenario.signupsAndPer': {
    en: '{n} signups · €{cost} per signup',
    nl: '{n} inschrijvingen · €{cost} per inschrijving',
    fr: '{n} inscriptions · €{cost} par inscription',
    de: '{n} Anmeldungen · €{cost} pro Anmeldung',
  },

  // ─── Cost calculator page ────────────────────────────────────────────
  'cost.title': {
    en: 'Cost calculator',
    nl: 'Kostencalculator',
    fr: 'Calculateur de coûts',
    de: 'Kostenrechner',
  },
  'cost.empty': {
    en: 'Create a campaign first.',
    nl: 'Maak eerst een campagne aan.',
    fr: 'Créez d\'abord une campagne.',
    de: 'Erstelle zuerst eine Kampagne.',
  },
  'cost.campaign': {
    en: 'Campaign',
    nl: 'Campagne',
    fr: 'Campagne',
    de: 'Kampagne',
  },
  'cost.targetSignups': {
    en: 'Target signups',
    nl: 'Doel inschrijvingen',
    fr: 'Inscriptions cibles',
    de: 'Ziel Anmeldungen',
  },
  'cost.milestoneRates': {
    en: 'Conversion rate per milestone',
    nl: 'Conversieratio per milestone',
    fr: 'Taux de conversion par palier',
    de: 'Konversionsrate pro Meilenstein',
  },
  'cost.milestoneSliderLabel': {
    en: '{n} referrals — {pct}%',
    nl: '{n} referrals — {pct}%',
    fr: '{n} parrainages — {pct}%',
    de: '{n} Empfehlungen — {pct}%',
  },
  'cost.projection': {
    en: 'Reward projection',
    nl: 'Reward projectie',
    fr: 'Projection des récompenses',
    de: 'Belohnungsprojektion',
  },
  'cost.col.reward': {
    en: 'Reward',
    nl: 'Reward',
    fr: 'Récompense',
    de: 'Belohnung',
  },
  'cost.col.trigger': {
    en: 'Trigger',
    nl: 'Trigger',
    fr: 'Déclencheur',
    de: 'Auslöser',
  },
  'cost.col.winners': {
    en: 'Winners',
    nl: 'Winnaars',
    fr: 'Gagnants',
    de: 'Gewinner',
  },
  'cost.col.costPerWinner': {
    en: 'Cost / winner',
    nl: 'Kost / winnaar',
    fr: 'Coût / gagnant',
    de: 'Kosten / Gewinner',
  },
  'cost.col.total': {
    en: 'Total',
    nl: 'Totaal',
    fr: 'Total',
    de: 'Gesamt',
  },
  'cost.totalRow': {
    en: 'Total',
    nl: 'Totaal',
    fr: 'Total',
    de: 'Gesamt',
  },
  'cost.perSignupRow': {
    en: 'Cost per signup',
    nl: 'Kost per inschrijving',
    fr: 'Coût par inscription',
    de: 'Kosten pro Anmeldung',
  },
  'cost.cap': { en: 'cap', nl: 'cap', fr: 'plafond', de: 'cap' },
  'cost.cappedTitle': {
    en: 'Capped at max {n}',
    nl: 'Gecapt op max {n}',
    fr: 'Plafonné à max {n}',
    de: 'Begrenzt auf max {n}',
  },
  'cost.scenariosExplainer': {
    en: 'Three scenarios show what the campaign costs if it underperforms (conservative = half as many signups), exactly hits your goal (expected), or beats it (optimistic = twice as many signups). Cost per signup usually stays stable, except where reward caps or fixed top-N rewards break linear scaling.',
    nl: 'Drie scenario\'s tonen wat de campagne kost als ze minder goed loopt (conservatief = half zoveel inschrijvingen), precies jouw doel haalt (verwacht), of beter dan gehoopt (optimistisch = dubbel zoveel inschrijvingen). De kost per inschrijving blijft meestal stabiel, behalve waar reward-caps of vaste top-N rewards de schaalbaarheid doorbreken.',
    fr: 'Trois scénarios montrent le coût de la campagne si elle est sous-performante (conservateur = moitié moins d\'inscriptions), atteint exactement votre objectif (prévu), ou dépasse les attentes (optimiste = deux fois plus d\'inscriptions). Le coût par inscription reste généralement stable, sauf lorsque les plafonds de récompense ou les récompenses top-N à nombre fixe brisent la mise à l\'échelle linéaire.',
    de: 'Drei Szenarien zeigen, was die Kampagne kostet, wenn sie schlechter läuft (konservativ = halb so viele Anmeldungen), genau dein Ziel erreicht (erwartet) oder besser läuft als erhofft (optimistisch = doppelt so viele Anmeldungen). Die Kosten pro Anmeldung bleiben meist stabil, außer dort, wo Reward-Limits oder feste Top-N-Belohnungen die lineare Skalierung durchbrechen.',
  },
  'cost.scenarios.conservativeHint': {
    en: '½ of your goal — what if the campaign underperforms?',
    nl: '½ van je doel — wat als de campagne tegenvalt?',
    fr: '½ de votre objectif — et si la campagne sous-performe ?',
    de: '½ deines Ziels — was, wenn die Kampagne enttäuscht?',
  },
  'cost.scenarios.expectedHint': {
    en: 'Your entered goal.',
    nl: 'Jouw ingegeven doel.',
    fr: 'Votre objectif saisi.',
    de: 'Dein eingegebenes Ziel.',
  },
  'cost.scenarios.optimisticHint': {
    en: '2× your goal — what if it goes viral?',
    nl: '2× je doel — wat als ze viraal gaat?',
    fr: '2× votre objectif — et si ça devient viral ?',
    de: '2× dein Ziel — was, wenn es viral geht?',
  },

  // ─── Content generator page ──────────────────────────────────────────
  'content.title': {
    en: 'Content Generator',
    nl: 'Content Generator',
    fr: 'Générateur de contenu',
    de: 'Content-Generator',
  },
  'content.empty': {
    en: 'Create a campaign first to generate content.',
    nl: 'Maak eerst een campagne aan om content te kunnen genereren.',
    fr: 'Créez d\'abord une campagne pour générer du contenu.',
    de: 'Erstelle zuerst eine Kampagne, um Inhalte zu generieren.',
  },
  'content.notice': {
    en: 'AI usage is paid. Each generated post is charged to your organisation balance based on actual token usage (visible under Payments → Features → generate-social-post). The cost is logged per platform after each generation. Posts are streamed live as they\'re generated.',
    nl: 'AI-gebruik is betalend. Elke gegenereerde post wordt afgeboekt op je organisatiebalans op basis van het werkelijke tokenverbruik (zichtbaar in Payments → Features → generate-social-post). De kost staat per platform in de log na elke generatie. Posts worden live gestreamd terwijl ze gegenereerd worden.',
    fr: 'L\'utilisation de l\'IA est payante. Chaque post généré est débité du solde de votre organisation selon la consommation réelle de tokens (visible dans Payments → Features → generate-social-post). Le coût est journalisé par plateforme après chaque génération. Les posts sont diffusés en direct pendant la génération.',
    de: 'Die KI-Nutzung ist kostenpflichtig. Jeder generierte Beitrag wird basierend auf dem tatsächlichen Token-Verbrauch von deinem Organisationsguthaben abgebucht (sichtbar unter Payments → Features → generate-social-post). Die Kosten werden pro Plattform nach jeder Generierung protokolliert. Beiträge werden live gestreamt während sie generiert werden.',
  },
  'content.notice.bold': {
    en: 'AI usage is paid.',
    nl: 'AI-gebruik is betalend.',
    fr: 'L\'utilisation de l\'IA est payante.',
    de: 'Die KI-Nutzung ist kostenpflichtig.',
  },
  'content.campaign': {
    en: 'Campaign',
    nl: 'Campagne',
    fr: 'Campagne',
    de: 'Kampagne',
  },
  'content.language': {
    en: 'Language',
    nl: 'Taal',
    fr: 'Langue',
    de: 'Sprache',
  },
  'content.tone': {
    en: 'Tone',
    nl: 'Toon',
    fr: 'Ton',
    de: 'Tonfall',
  },
  'content.platforms': {
    en: 'Platforms',
    nl: 'Platforms',
    fr: 'Plateformes',
    de: 'Plattformen',
  },
  'content.generate': {
    en: 'Generate {n} post(s)',
    nl: 'Genereer {n} post(s)',
    fr: 'Générer {n} post(s)',
    de: '{n} Beitrag/Beiträge generieren',
  },
  'content.generating': {
    en: 'Generating…',
    nl: 'Genereren…',
    fr: 'Génération…',
    de: 'Generieren…',
  },
  'content.regenerate': {
    en: 'Regenerate',
    nl: 'Regenereer',
    fr: 'Régénérer',
    de: 'Neu generieren',
  },
  'content.regenerateWithFeedback': {
    en: 'Regenerate with feedback',
    nl: 'Regenereer met opmerkingen',
    fr: 'Régénérer avec commentaires',
    de: 'Mit Feedback neu generieren',
  },
  'content.feedbackLabel': {
    en: 'Feedback for the next regeneration',
    nl: 'Opmerkingen voor de hergeneratie',
    fr: 'Commentaires pour la régénération',
    de: 'Feedback für die nächste Generierung',
  },
  'content.feedbackPlaceholder': {
    en: 'e.g. "make it shorter", "less marketing-y", "add a stat"',
    nl: 'bv. "maak korter", "minder marketing-toon", "voeg een cijfer toe"',
    fr: 'p. ex. « plus court », « moins marketing », « ajoute un chiffre »',
    de: 'z. B. „kürzer", „weniger Marketing-Sprache", „eine Zahl hinzufügen"',
  },
  'content.feedbackHint': {
    en: 'Leave empty to regenerate without changes — the new text will use the same tone, language and prompt.',
    nl: 'Laat leeg om opnieuw te genereren zonder wijzigingen — de nieuwe tekst gebruikt dezelfde toon, taal en prompt.',
    fr: 'Laisser vide pour régénérer sans modification — le nouveau texte utilisera le même ton, la même langue et le même prompt.',
    de: 'Leer lassen, um ohne Änderungen neu zu generieren — der neue Text verwendet denselben Ton, dieselbe Sprache und denselben Prompt.',
  },
  'content.cost': {
    en: 'Cost: {n} tokens',
    nl: 'Kost: {n} tokens',
    fr: 'Coût : {n} tokens',
    de: 'Kosten: {n} Tokens',
  },
  'content.costEur': {
    en: 'Cost: €{eur}',
    nl: 'Kost: €{eur}',
    fr: 'Coût : €{eur}',
    de: 'Kosten: €{eur}',
  },
  'content.costBreakdown': {
    en: '{dotbots} DotBots tokens · AI: {input} in + {output} out',
    nl: '{dotbots} DotBots tokens · AI: {input} in + {output} uit',
    fr: '{dotbots} jetons DotBots · IA : {input} in + {output} out',
    de: '{dotbots} DotBots-Tokens · KI: {input} in + {output} out',
  },
  'content.copyText': {
    en: 'Copy text',
    nl: 'Kopieer tekst',
    fr: 'Copier le texte',
    de: 'Text kopieren',
  },
  'content.copied': {
    en: 'Copied',
    nl: 'Gekopieerd',
    fr: 'Copié',
    de: 'Kopiert',
  },
  'tone.informal': {
    en: 'Informal',
    nl: 'Informeel',
    fr: 'Informel',
    de: 'Informell',
  },
  'tone.formal': {
    en: 'Formal',
    nl: 'Formeel',
    fr: 'Formel',
    de: 'Formell',
  },
  'tone.enthusiastic': {
    en: 'Enthusiastic',
    nl: 'Enthousiast',
    fr: 'Enthousiaste',
    de: 'Enthusiastisch',
  },
  'tone.business': {
    en: 'Business',
    nl: 'Zakelijk',
    fr: 'Professionnel',
    de: 'Geschäftlich',
  },
  'contentStatus.pending': {
    en: 'Waiting…',
    nl: 'Wacht…',
    fr: 'En attente…',
    de: 'Warten…',
  },
  'contentStatus.generating': {
    en: 'Generating…',
    nl: 'Genereren…',
    fr: 'Génération…',
    de: 'Generieren…',
  },
  'contentStatus.done': {
    en: 'Done',
    nl: 'Klaar',
    fr: 'Terminé',
    de: 'Fertig',
  },
  'contentStatus.error': {
    en: 'Error',
    nl: 'Fout',
    fr: 'Erreur',
    de: 'Fehler',
  },
  'contentError.featureNotFound': {
    en: 'Feature "generate-social-post" is not yet configured in DotBots Boutique → Payments → Features. Set a price there for the feature code "generate-social-post".',
    nl: 'Feature "generate-social-post" is nog niet geconfigureerd in DotBots Boutique → Payments → Features. Stel daar een prijs in voor de feature code "generate-social-post".',
    fr: 'La fonctionnalité "generate-social-post" n\'est pas encore configurée dans DotBots Boutique → Payments → Features. Définissez-y un prix pour le code de fonctionnalité "generate-social-post".',
    de: 'Feature "generate-social-post" ist in DotBots Boutique → Payments → Features noch nicht konfiguriert. Lege dort einen Preis für den Feature-Code "generate-social-post" fest.',
  },
  'contentError.insufficientBalance': {
    en: 'Your organisation has insufficient DotBots tokens. Top up your platform balance.',
    nl: 'Je organisatie heeft onvoldoende DotBots tokens. Top up via je platform balans.',
    fr: 'Votre organisation n\'a pas assez de tokens DotBots. Rechargez votre solde sur la plateforme.',
    de: 'Deine Organisation hat zu wenig DotBots-Tokens. Lade dein Plattform-Guthaben auf.',
  },
  'contentError.providerNotConfigured': {
    en: 'The AI provider is not configured on the platform — contact your admin.',
    nl: 'De AI provider is niet geconfigureerd op het platform — neem contact op met je beheerder.',
    fr: 'Le fournisseur d\'IA n\'est pas configuré sur la plateforme — contactez votre administrateur.',
    de: 'Der KI-Anbieter ist nicht auf der Plattform konfiguriert — wende dich an deinen Administrator.',
  },
  'contentError.timeout': {
    en: 'The AI call was interrupted. Try again.',
    nl: 'De AI call werd onderbroken. Probeer opnieuw.',
    fr: 'L\'appel IA a été interrompu. Réessayez.',
    de: 'Der KI-Aufruf wurde unterbrochen. Versuche es erneut.',
  },
  'contentError.budgetExceeded': {
    en: 'Your budget for this feature is reached for this period.',
    nl: 'Je budget voor deze feature is bereikt voor deze periode.',
    fr: 'Votre budget pour cette fonctionnalité est atteint pour cette période.',
    de: 'Dein Budget für dieses Feature ist für diesen Zeitraum erreicht.',
  },
  'contentError.generic': {
    en: 'Generation failed.',
    nl: 'Generatie mislukt.',
    fr: 'La génération a échoué.',
    de: 'Generierung fehlgeschlagen.',
  },

  // ─── Campaign detail page ────────────────────────────────────────────
  'detail.allCampaigns': {
    en: '← All campaigns',
    nl: '← Alle campagnes',
    fr: '← Toutes les campagnes',
    de: '← Alle Kampagnen',
  },
  'detail.endsIn': {
    en: 'Ends in {d}d {h}h',
    nl: 'Eindigt over {d}d {h}u',
    fr: 'Se termine dans {d}j {h}h',
    de: 'Endet in {d}T {h}h',
  },
  'detail.triggerLabel': {
    en: 'trigger',
    nl: 'trigger',
    fr: 'déclencheur',
    de: 'Auslöser',
  },
  'detail.launch': {
    en: 'Launch',
    nl: 'Lanceer',
    fr: 'Lancer',
    de: 'Starten',
  },
  'detail.resume': {
    en: 'Resume',
    nl: 'Hervat',
    fr: 'Reprendre',
    de: 'Fortsetzen',
  },
  'detail.pause': {
    en: 'Pause',
    nl: 'Pauzeer',
    fr: 'Mettre en pause',
    de: 'Pausieren',
  },
  'detail.recalculate': {
    en: 'Recalculate',
    nl: 'Herbereken',
    fr: 'Recalculer',
    de: 'Neu berechnen',
  },
  'stat.signups': {
    en: 'Signups',
    nl: 'Inschrijvingen',
    fr: 'Inscriptions',
    de: 'Anmeldungen',
  },
  'stat.activated': {
    en: 'Activated',
    nl: 'Geactiveerd',
    fr: 'Activé',
    de: 'Aktiviert',
  },
  'stat.rewardsHandedOut': {
    en: 'Rewards handed out',
    nl: 'Rewards uitgereikt',
    fr: 'Récompenses distribuées',
    de: 'Belohnungen vergeben',
  },
  'stat.realCost': {
    en: 'Real cost',
    nl: 'Werkelijke kost',
    fr: 'Coût réel',
    de: 'Tatsächliche Kosten',
  },
  'stat.projected': {
    en: 'Projected: €{n}',
    nl: 'Geprojecteerd: €{n}',
    fr: 'Projeté : €{n}',
    de: 'Prognose: €{n}',
  },
  'detail.signupsPerDay': {
    en: 'Signups per day',
    nl: 'Registraties per dag',
    fr: 'Inscriptions par jour',
    de: 'Anmeldungen pro Tag',
  },
  'detail.noSignupsYet': {
    en: 'No signups yet.',
    nl: 'Nog geen registraties.',
    fr: 'Pas encore d\'inscriptions.',
    de: 'Noch keine Anmeldungen.',
  },
  'detail.signupSource': {
    en: 'Signup source',
    nl: 'Bron van inschrijvingen',
    fr: 'Source des inscriptions',
    de: 'Quelle der Anmeldungen',
  },
  'detail.noSourceData': {
    en: 'No source data yet.',
    nl: 'Nog geen bron-data.',
    fr: 'Pas encore de données de source.',
    de: 'Noch keine Quellendaten.',
  },
  'detail.rewardBreakdown': {
    en: 'Reward breakdown',
    nl: 'Reward breakdown',
    fr: 'Détail des récompenses',
    de: 'Belohnungsaufschlüsselung',
  },
  'detail.col.winners': {
    en: 'Winners',
    nl: 'Winnaars',
    fr: 'Gagnants',
    de: 'Gewinner',
  },
  'detail.col.costPerWinner': {
    en: 'Cost / winner',
    nl: 'Kost / winnaar',
    fr: 'Coût / gagnant',
    de: 'Kosten / Gewinner',
  },
  'detail.col.total': {
    en: 'Total',
    nl: 'Totaal',
    fr: 'Total',
    de: 'Gesamt',
  },
  'detail.col.projected': {
    en: 'Projected',
    nl: 'Geprojecteerd',
    fr: 'Projeté',
    de: 'Prognose',
  },
  'detail.noRewards': {
    en: 'No rewards configured.',
    nl: 'Geen rewards geconfigureerd.',
    fr: 'Aucune récompense configurée.',
    de: 'Keine Belohnungen konfiguriert.',
  },
  'budget.statusTitle': {
    en: 'Budget status',
    nl: 'Budget status',
    fr: 'État du budget',
    de: 'Budget-Status',
  },
  'budget.atHardCap': {
    en: 'At hard cap: {action}',
    nl: 'Bij hard cap: {action}',
    fr: 'Au plafond strict : {action}',
    de: 'Bei Hard Cap: {action}',
  },
  'budget.usedOf': {
    en: '€{used} / €{total} used ({pct}%)',
    nl: '€{used} / €{total} gebruikt ({pct}%)',
    fr: '€{used} / €{total} utilisés ({pct}%)',
    de: '€{used} / €{total} verbraucht ({pct}%)',
  },
  'budget.softCapAt': {
    en: 'Soft cap at €{amount} ({pct}%)',
    nl: 'Soft cap bij €{amount} ({pct}%)',
    fr: 'Plafond souple à €{amount} ({pct}%)',
    de: 'Soft Cap bei €{amount} ({pct}%)',
  },
  'budget.softCapReached': {
    en: '⚠ Soft cap reached — a warning has been logged.',
    nl: '⚠ Soft cap bereikt — een waarschuwing is gelogd.',
    fr: '⚠ Plafond souple atteint — un avertissement a été journalisé.',
    de: '⚠ Soft Cap erreicht — eine Warnung wurde protokolliert.',
  },
  'budget.hardCapReached': {
    en: '🛑 Hard cap reached — action: {action}',
    nl: '🛑 Hard cap bereikt — actie: {action}',
    fr: '🛑 Plafond strict atteint — action : {action}',
    de: '🛑 Hard Cap erreicht — Aktion: {action}',
  },
  'budget.todayUsage': {
    en: 'Today: €{today} / €{cap}',
    nl: 'Vandaag: €{today} / €{cap}',
    fr: 'Aujourd\'hui : €{today} / €{cap}',
    de: 'Heute: €{today} / €{cap}',
  },
  'budget.capReached': {
    en: 'Cap reached',
    nl: 'Cap bereikt',
    fr: 'Plafond atteint',
    de: 'Cap erreicht',
  },
  'budget.col.reward': {
    en: 'Reward',
    nl: 'Reward',
    fr: 'Récompense',
    de: 'Belohnung',
  },
  'budget.col.winners': {
    en: 'Winners',
    nl: 'Winnaars',
    fr: 'Gagnants',
    de: 'Gewinner',
  },
  'budget.col.max': {
    en: 'Max',
    nl: 'Max',
    fr: 'Max',
    de: 'Max',
  },
  'budget.col.status': {
    en: 'Status',
    nl: 'Status',
    fr: 'Statut',
    de: 'Status',
  },
  'budget.unlimited': {
    en: '∞ Unlimited',
    nl: '∞ Onbeperkt',
    fr: '∞ Illimité',
    de: '∞ Unbegrenzt',
  },
  'budget.exhausted': {
    en: '🔴 Exhausted',
    nl: '🔴 Uitgeput',
    fr: '🔴 Épuisé',
    de: '🔴 Erschöpft',
  },
  'budget.almostFull': {
    en: '🟠 Almost full',
    nl: '🟠 Bijna vol',
    fr: '🟠 Presque plein',
    de: '🟠 Fast voll',
  },
  'budget.activeReward': {
    en: '🟢 Active',
    nl: '🟢 Actief',
    fr: '🟢 Actif',
    de: '🟢 Aktiv',
  },

  // ─── Leaderboard component ───────────────────────────────────────────
  'lb.title': {
    en: 'Leaderboard',
    nl: 'Leaderboard',
    fr: 'Classement',
    de: 'Bestenliste',
  },
  'lb.signupsCount': {
    en: '{n} signups',
    nl: '{n} inschrijvingen',
    fr: '{n} inscriptions',
    de: '{n} Anmeldungen',
  },
  'lb.minNeeded': {
    en: '{n} more signups needed for top-N rewards',
    nl: 'Nog {n} inschrijvingen nodig voor top-N rewards',
    fr: 'Encore {n} inscriptions nécessaires pour les récompenses top-N',
    de: 'Noch {n} Anmeldungen für Top-N-Belohnungen erforderlich',
  },
  'lb.minNeededPublic': {
    en: '{n} more signups needed for top-N rewards!',
    nl: 'Nog {n} inschrijvingen nodig voor top-N rewards!',
    fr: 'Encore {n} inscriptions nécessaires pour les récompenses top-N !',
    de: 'Noch {n} Anmeldungen für Top-N-Belohnungen erforderlich!',
  },
  'lb.tabDaily': {
    en: 'Today',
    nl: 'Vandaag',
    fr: "Aujourd'hui",
    de: 'Heute',
  },
  'lb.tabWeekly': {
    en: 'This week',
    nl: 'Deze week',
    fr: 'Cette semaine',
    de: 'Diese Woche',
  },
  'lb.tabMonthly': {
    en: 'This month',
    nl: 'Deze maand',
    fr: 'Ce mois',
    de: 'Diesen Monat',
  },
  'lb.tabOverall': {
    en: 'All time',
    nl: 'Alles',
    fr: 'Tout le temps',
    de: 'Gesamt',
  },
  'lb.col.referrals': {
    en: 'Referrals',
    nl: 'Referrals',
    fr: 'Parrainages',
    de: 'Empfehlungen',
  },
  'lb.col.name': {
    en: 'Name',
    nl: 'Naam',
    fr: 'Nom',
    de: 'Name',
  },
  'lb.empty': {
    en: 'No referrals yet in this period.',
    nl: 'Nog geen referrals in deze periode.',
    fr: 'Pas encore de parrainages dans cette période.',
    de: 'Noch keine Empfehlungen in diesem Zeitraum.',
  },
  'lb.emptyAll': {
    en: 'No referrals yet.',
    nl: 'Nog geen referrals.',
    fr: 'Pas encore de parrainages.',
    de: 'Noch keine Empfehlungen.',
  },
  'lb.loading': {
    en: 'Loading leaderboard…',
    nl: 'Leaderboard laden…',
    fr: 'Chargement du classement…',
    de: 'Bestenliste lädt…',
  },

  // ─── Public leaderboard page ─────────────────────────────────────────
  'public.noCampaignId': {
    en: 'No campaign ID provided.',
    nl: 'Geen campagne ID opgegeven.',
    fr: 'Aucun ID de campagne fourni.',
    de: 'Keine Kampagnen-ID angegeben.',
  },

  // ─── Participant portal ──────────────────────────────────────────────
  'portal.welcome': {
    en: 'Welcome',
    nl: 'Welkom',
    fr: 'Bienvenue',
    de: 'Willkommen',
  },
  'portal.rewardsWonBadge': {
    en: '{n} rewards won',
    nl: '{n} rewards gewonnen',
    fr: '{n} récompenses gagnées',
    de: '{n} Belohnungen gewonnen',
  },
  'portal.myCampaigns': {
    en: 'My campaigns',
    nl: 'Mijn campagnes',
    fr: 'Mes campagnes',
    de: 'Meine Kampagnen',
  },
  'portal.notRegistered': {
    en: "You haven't joined any campaigns yet.",
    nl: 'Je neemt nog niet deel aan een campagne.',
    fr: "Vous n'avez encore rejoint aucune campagne.",
    de: 'Du nimmst noch an keiner Kampagne teil.',
  },
  'portal.cardSummary': {
    en: '{count} referrals · position {pos}/{total}',
    nl: '{count} referrals · positie {pos}/{total}',
    fr: '{count} parrainages · position {pos}/{total}',
    de: '{count} Empfehlungen · Position {pos}/{total}',
  },
  'portal.cardSummaryNoPosition': {
    en: '{count} referrals',
    nl: '{count} referrals',
    fr: '{count} parrainages',
    de: '{count} Empfehlungen',
  },
  'portal.nextMilestoneCard': {
    en: '{n} more for {label}',
    nl: 'Nog {n} voor {label}',
    fr: 'Encore {n} pour {label}',
    de: 'Noch {n} für {label}',
  },
  'portal.endsIn': {
    en: 'Ends in {d}d {h}h',
    nl: 'Eindigt over {d}d {h}u',
    fr: 'Se termine dans {d}j {h}h',
    de: 'Endet in {d}T {h}h',
  },
  'portal.yourReferrals': {
    en: 'Your referrals',
    nl: 'Jouw referrals',
    fr: 'Vos parrainages',
    de: 'Deine Empfehlungen',
  },
  'portal.leaderboardCard': {
    en: 'Leaderboard',
    nl: 'Leaderboard',
    fr: 'Classement',
    de: 'Bestenliste',
  },
  'portal.outOfTotal': {
    en: 'Of {n}',
    nl: 'Van {n}',
    fr: 'Sur {n}',
    de: 'Von {n}',
  },
  'portal.rewardsWonStat': {
    en: 'Rewards won',
    nl: 'Rewards gewonnen',
    fr: 'Récompenses gagnées',
    de: 'Gewonnene Belohnungen',
  },
  'portal.yourCode': {
    en: 'Your code',
    nl: 'Jouw code',
    fr: 'Votre code',
    de: 'Dein Code',
  },
  'portal.copyCode': {
    en: 'Copy code',
    nl: 'Kopieer code',
    fr: 'Copier le code',
    de: 'Code kopieren',
  },
  'portal.copied': {
    en: 'Copied!',
    nl: 'Gekopieerd!',
    fr: 'Copié !',
    de: 'Kopiert!',
  },
  'portal.nextMilestone': {
    en: 'Next milestone: {label}',
    nl: 'Volgende milestone: {label}',
    fr: 'Prochain palier : {label}',
    de: 'Nächster Meilenstein: {label}',
  },
  'portal.referralsRemaining': {
    en: '{n} more referrals',
    nl: 'Nog {n} referrals',
    fr: 'Encore {n} parrainages',
    de: 'Noch {n} Empfehlungen',
  },
  'portal.luckyDrawTitle': {
    en: '🎲 Lucky draw',
    nl: '🎲 Lucky draw',
    fr: '🎲 Tirage au sort',
    de: '🎲 Lucky Draw',
  },
  'portal.noLuckyDraw': {
    en: 'No lucky draw for this campaign.',
    nl: 'Geen lucky draw voor deze campagne.',
    fr: 'Pas de tirage au sort pour cette campagne.',
    de: 'Kein Lucky Draw für diese Kampagne.',
  },
  'portal.luckyDrawWon': {
    en: '🎉 You won!',
    nl: '🎉 Je hebt gewonnen!',
    fr: '🎉 Vous avez gagné !',
    de: '🎉 Du hast gewonnen!',
  },
  'portal.luckyDrawLost': {
    en: 'No lucky draw win.',
    nl: 'Geen lucky draw winst.',
    fr: 'Pas de gain au tirage au sort.',
    de: 'Kein Lucky-Draw-Gewinn.',
  },
  'portal.milestonesTitle': {
    en: '🏅 Milestones',
    nl: '🏅 Milestones',
    fr: '🏅 Paliers',
    de: '🏅 Meilensteine',
  },
  'portal.noMilestones': {
    en: 'No milestones.',
    nl: 'Geen milestones.',
    fr: 'Aucun palier.',
    de: 'Keine Meilensteine.',
  },
  'portal.milestoneRow': {
    en: '{trigger} referrals — {label}',
    nl: '{trigger} referrals — {label}',
    fr: '{trigger} parrainages — {label}',
    de: '{trigger} Empfehlungen — {label}',
  },
  'portal.milestoneRemaining': {
    en: '(still {n})',
    nl: '(nog {n})',
    fr: '(encore {n})',
    de: '(noch {n})',
  },
  'portal.topNTitle': {
    en: '🏆 Top-N rewards',
    nl: '🏆 Top-N rewards',
    fr: '🏆 Récompenses top-N',
    de: '🏆 Top-N-Belohnungen',
  },
  'portal.topN.col.reward': {
    en: 'Reward',
    nl: 'Reward',
    fr: 'Récompense',
    de: 'Belohnung',
  },
  'portal.topN.col.period': {
    en: 'Period',
    nl: 'Periode',
    fr: 'Période',
    de: 'Zeitraum',
  },
  'portal.topN.col.position': {
    en: 'Your position',
    nl: 'Jouw positie',
    fr: 'Votre position',
    de: 'Deine Position',
  },
  'portal.topN.col.status': {
    en: 'Status',
    nl: 'Status',
    fr: 'Statut',
    de: 'Status',
  },
  'portal.topN.finalStandings': {
    en: 'Final standings',
    nl: 'Eindstand',
    fr: 'Classement final',
    de: 'Endstand',
  },
  'portal.topN.onCourse': {
    en: 'On course!',
    nl: 'Op koers!',
    fr: 'Sur la bonne voie !',
    de: 'Auf Kurs!',
  },
  'portal.topN.outsideTopN': {
    en: 'Outside top-{n}',
    nl: 'Buiten top-{n}',
    fr: 'Hors top-{n}',
    de: 'Außerhalb Top-{n}',
  },
  'portal.top10': {
    en: 'Top 10',
    nl: 'Top 10',
    fr: 'Top 10',
    de: 'Top 10',
  },
  'portal.you': {
    en: 'you',
    nl: 'jij',
    fr: 'vous',
    de: 'du',
  },
  'portal.noUid': {
    en: 'No userId provided. The link must contain ?uid=...',
    nl: 'Geen userId opgegeven. De link moet ?uid=... bevatten.',
    fr: 'Aucun userId fourni. Le lien doit contenir ?uid=...',
    de: 'Keine userId angegeben. Der Link muss ?uid=... enthalten.',
  },
  'portal.col.rank': {
    en: '#',
    nl: '#',
    fr: '#',
    de: '#',
  },
  'portal.col.name': {
    en: 'Name',
    nl: 'Naam',
    fr: 'Nom',
    de: 'Name',
  },
  'portal.col.referrals': {
    en: 'Referrals',
    nl: 'Referrals',
    fr: 'Parrainages',
    de: 'Empfehlungen',
  },

  // ─── Share panel ─────────────────────────────────────────────────────
  'share.title': {
    en: 'Share your referral link',
    nl: 'Deel je referral link',
    fr: 'Partagez votre lien de parrainage',
    de: 'Teile deinen Empfehlungslink',
  },
  'share.codeBadge': {
    en: 'Code: {code}',
    nl: 'Code: {code}',
    fr: 'Code : {code}',
    de: 'Code: {code}',
  },
  'share.copyLink': {
    en: 'Copy',
    nl: 'Kopieer',
    fr: 'Copier',
    de: 'Kopieren',
  },
  'share.copied': {
    en: 'Copied!',
    nl: 'Gekopieerd!',
    fr: 'Copié !',
    de: 'Kopiert!',
  },
  'share.share': {
    en: 'Share',
    nl: 'Share',
    fr: 'Partager',
    de: 'Teilen',
  },
  'share.examples': {
    en: 'Sample texts',
    nl: 'Voorbeeldteksten',
    fr: 'Textes types',
    de: 'Beispieltexte',
  },
  'share.copyText': {
    en: 'Copy text',
    nl: 'Kopieer tekst',
    fr: 'Copier le texte',
    de: 'Text kopieren',
  },
  'share.copiedText': {
    en: 'Copied',
    nl: 'Gekopieerd',
    fr: 'Copié',
    de: 'Kopiert',
  },
  'share.platform.base': {
    en: 'Base link',
    nl: 'Basis link',
    fr: 'Lien de base',
    de: 'Basis-Link',
  },
};

export function t(key: string): string {
  const entry = T[key];
  if (!entry) return key;
  return entry[lang] ?? entry.en ?? key;
}

// Replace {placeholder} tokens with values. Strings without placeholders
// pass through unchanged so call sites can always use tp() safely.
export function tp(key: string, params: Record<string, string | number>): string {
  let value = t(key);
  for (const [k, v] of Object.entries(params)) {
    value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  }
  return value;
}
